import {
  ProjectRole,
  ProjectUpgradeInfo,
  TRIAL_RESTRICTED_PERMISSIONS,
  PERMISSION_FEATURE_MAP,
} from "./project.types";
import { Permissions } from "utils/permissions/permissionConstants";

export const UNLICENSED_PROJECT_LOCKED_MESSAGE =
  "Upgrade to unlock unlimited scenes, PDF & FDX exports, version history, share links and premium AI.";

/** When a Writer OTP license blocks schedule/budget AI — upgrade path is Studio. */
export const WRITER_OTP_STUDIO_SCHEDULE_BUDGET_MESSAGE =
  "AI schedule and budget reruns are included with a Studio license. Upgrade from Writer to unlock production scheduling and budgeting tools.";

/** Extra line after API insufficient-credits message for unlicensed projects (source-aware). */
export function getUnlicensedInsufficientAiCreditsFollowUp(createSource?: string): string {
  return createSource === "blank"
    ? "Unlock unlimited AI runs and premium features."
    : "Unlock to keep iterating with AI on your script.";
}

/** @deprecated use UNLICENSED_PROJECT_LOCKED_MESSAGE */
export const FREE_LICENSE_PREMIUM_LOCKED_MESSAGE = UNLICENSED_PROJECT_LOCKED_MESSAGE;

/** Shown when finalize revision is disabled on a free workspace slot (one finalize per script). */
export const FREE_LICENSE_REVISION_FINALIZE_LIMIT_MESSAGE =
  "This project is on a free workspace slot: you can finalize a revision only once. Purchase a per-project license to unlock additional finalizations.";

/** Short copy for premium / license affordances in compact UI (menus, icons). */
export const LICENSE_PURCHASE_MENU_TOOLTIP =
  "Purchase a per-project license in project settings to unlock this feature on RivetAI.";

export const createProjectUpgradeInfo = (
  permission: string,
  currentRole?: ProjectRole,
  userRole?: string,
  isTrialProject: boolean = false,
  freeLicensePremiumLocked: boolean = false
): ProjectUpgradeInfo => {
  const isTrialRestricted = TRIAL_RESTRICTED_PERMISSIONS.includes(permission);
  const featureName = PERMISSION_FEATURE_MAP[permission] || permission;

  if (freeLicensePremiumLocked && isTrialRestricted) {
    return {
      message: UNLICENSED_PROJECT_LOCKED_MESSAGE,
      action: "upgrade",
      requiredPlan: "Project license",
      currentPlan: "Unlicensed",
      features: [featureName],
      permission,
      isTrialRestricted: true,
    };
  }

  if (isTrialRestricted && isTrialProject) {
    return {
      message: `Not available on unlicensed projects. Purchase a license or upgrade your plan to unlock this feature.`,
      action: "upgrade",
      requiredPlan: "Paid Plan",
      currentPlan: "Unlicensed",
      features: [featureName],
      permission,
      isTrialRestricted: true,
    };
  }

  switch (permission) {
    case Permissions.PROJECT_UPDATE:
      return {
        message: "Project settings access required. Contact your workspace admin.",
        action: "contact_admin",
        requiredRole: ProjectRole.EDITOR,
        currentPlan: isTrialProject ? "Trial" : "Paid",
        features: [featureName],
        permission,
        isTrialRestricted: false,
      };

    case Permissions.PROJECT_SCRIPT_UPDATE:
      return {
        message:
          "Screenplay editing access required. Ask a workspace admin to grant Script editor or Editor on this project.",
        action: "contact_admin",
        requiredRole: ProjectRole.SCRIPT_EDITOR,
        currentPlan: isTrialProject ? "Trial" : "Paid",
        features: [featureName],
        permission,
        isTrialRestricted: false,
      };

    case Permissions.PROJECT_TAG_UPDATE:
      return {
        message:
          "Tag editing access required. Ask a workspace admin to grant Script editor or Editor on this project.",
        action: "contact_admin",
        requiredRole: ProjectRole.SCRIPT_EDITOR,
        currentPlan: isTrialProject ? "Trial" : "Paid",
        features: [featureName],
        permission,
        isTrialRestricted: false,
      };

    case Permissions.PROJECT_SHARE_LINK_MANAGE:
      return {
        message: "Share link management requires Editor access on this project.",
        action: "contact_admin",
        requiredRole: ProjectRole.EDITOR,
        currentPlan: isTrialProject ? "Trial" : "Paid",
        features: [featureName],
        permission,
        isTrialRestricted: false,
      };

    case Permissions.PROJECT_DELETE:
      return {
        message: "Only project creators can delete projects. Contact your workspace admin.",
        action: "contact_admin",
        requiredRole: ProjectRole.CREATOR,
        currentPlan: isTrialProject ? "Trial" : "Paid",
        features: [featureName],
        permission,
        isTrialRestricted: false,
      };

    case Permissions.PROJECT_EXPORT:
    case Permissions.PROJECT_VERSION_CREATE:
    case Permissions.PROJECT_BUDGET_RERUN:
    case Permissions.PROJECT_SCHEDULE_RERUN:
      return {
        message: "Editor access required. Contact your workspace admin.",
        action: "contact_admin",
        requiredRole: ProjectRole.EDITOR,
        currentPlan: isTrialProject ? "Trial" : "Paid",
        features: [featureName],
        permission,
        isTrialRestricted: isTrialRestricted,
      };

    default:
      return {
        message: "Permission required. Contact your workspace admin.",
        action: "contact_admin",
        currentPlan: isTrialProject ? "Trial" : "Paid",
        features: [featureName],
        permission,
        isTrialRestricted: false,
      };
  }
};

export const getProjectUpgradeMessage = (
  permission: string,
  role?: ProjectRole,
  userRole?: string,
  isTrialProject: boolean = false,
  freeLicensePremiumLocked: boolean = false
): string => {
  const upgradeInfo = createProjectUpgradeInfo(
    permission,
    role,
    userRole,
    isTrialProject,
    freeLicensePremiumLocked
  );
  return upgradeInfo.message;
};

export const isTrialRestrictedPermission = (permission: string): boolean => {
  return TRIAL_RESTRICTED_PERMISSIONS.includes(permission);
};

export const getTrialRestrictedPermissions = (): string[] => {
  return [...TRIAL_RESTRICTED_PERMISSIONS];
};

export const getPermissionFeatureName = (permission: string): string => {
  return PERMISSION_FEATURE_MAP[permission] || permission;
};

export const createMultiPermissionUpgradeMessage = (
  permissions: string[],
  isTrialProject: boolean = false
): string => {
  if (permissions.length === 0) return "";

  const trialRestricted = permissions.filter(isTrialRestrictedPermission);
  const roleRestricted = permissions.filter((p) => !isTrialRestrictedPermission(p));

  if (isTrialProject && trialRestricted.length > 0) {
    const features = trialRestricted.map(getPermissionFeatureName);
    return `Not available on unlicensed projects: ${features.join(", ")}. Purchase a license or upgrade your plan to access.`;
  }

  if (roleRestricted.length > 0) {
    const features = roleRestricted.map(getPermissionFeatureName);
    return `Additional permissions required: ${features.join(", ")}. Contact your workspace admin.`;
  }

  return "Permission required. Contact your workspace admin.";
};
